import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

const ContractorSelectionCriteriaModal = React.memo(({ isOpen, onClose, onSaveSuccess, onSaveError }) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Note: quality, hse, performance, commercial, technical have weights (insurance/orientation are Yes/No checkboxes)
  const [weights, setWeights] = useState({
    quality_weight: '',
    hse_weight: '',
    performance_weight: '',
    commercial_weight: '',
    technical_weight: ''
  });
  const [totalWeight, setTotalWeight] = useState(0);

  // Fetch existing weights when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchWeights();
    }
  }, [isOpen]);

  // Calculate total weight whenever weights change
  useEffect(() => {
    const total = 
      (parseFloat(weights.quality_weight) || 0) +
      (parseFloat(weights.hse_weight) || 0) +
      (parseFloat(weights.performance_weight) || 0) +
      (parseFloat(weights.commercial_weight) || 0) +
      (parseFloat(weights.technical_weight) || 0);
    setTotalWeight(total);
  }, [weights]);

  const fetchWeights = async () => {
    try {
      setLoading(true);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        '/contractors/weights'
      );

      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setWeights({
          quality_weight: data.quality_weight?.toString() || '20',
          hse_weight: data.hse_weight?.toString() || '20',
          performance_weight: data.performance_weight?.toString() || '20',
          commercial_weight: data.commercial_weight?.toString() || '20',
          technical_weight: data.technical_weight?.toString() || '20'
        });
      }
    } catch (error) {
      console.error('Error fetching contractor selection weights:', error);
      // Start with default values if fetch fails (sum to 100%)
      setWeights({
        quality_weight: '20',
        hse_weight: '20',
        performance_weight: '20',
        commercial_weight: '20',
        technical_weight: '20'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    // Allow empty string or valid decimal numbers
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setWeights(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSave = async () => {
    // Validate total equals 100
    if (Math.abs(totalWeight - 100) > 0.01) {
      onSaveError(`Weights must sum to 100%. Current sum: ${totalWeight.toFixed(2)}%`);
      return;
    }

    try {
      setSaving(true);

      const dataToSave = {
        quality_weight: parseFloat(weights.quality_weight) || 0,
        hse_weight: parseFloat(weights.hse_weight) || 0,
        performance_weight: parseFloat(weights.performance_weight) || 0,
        commercial_weight: parseFloat(weights.commercial_weight) || 0,
        technical_weight: parseFloat(weights.technical_weight) || 0
      };

      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/contractors/weights',
        dataToSave
      );

      if (response.data.success) {
        onSaveSuccess('Contractor selection criteria weights saved successfully!');
        onClose();
      } else {
        throw new Error(response.data.message || 'Failed to save weights');
      }
    } catch (error) {
      console.error('Error saving contractor selection weights:', error);
      onSaveError(error.response?.data?.message || error.message || 'Failed to save weights');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const isValidTotal = Math.abs(totalWeight - 100) <= 0.01;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '2rem',
        width: '90%',
        maxWidth: '550px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ margin: 0, color: '#333' }}>
            🎯 Contractor Selection Criteria Weights
          </h3>
          <button
            onClick={handleClose}
            disabled={saving}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: saving ? 'not-allowed' : 'pointer',
              color: '#666',
              padding: '0.25rem',
              opacity: saving ? 0.5 : 1
            }}
          >
            ×
          </button>
        </div>

        <div style={{
          backgroundColor: '#e3f2fd',
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '1.5rem',
          fontSize: '0.9rem',
          color: '#1976d2'
        }}>
          <strong>Instructions:</strong>
          <br />
          <span style={{ fontSize: '0.85rem' }}>
            Set the weight (percentage) for each selection criterion. All weights must sum to exactly 100%.
            These weights will be used to calculate the overall score for each contractor.
          </span>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div style={{
              display: 'inline-block',
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #007bff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ marginTop: '1rem', color: '#666' }}>Loading weights...</p>
          </div>
        ) : (
          <>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              {/* Note: Insurance and Orientation are Yes/No checkboxes, not weighted scores */}
              
              {/* Quality Weight */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  color: '#333',
                  fontSize: '0.9rem'
                }}>
                  ⭐ Quality Weight (%)
                </label>
                <input
                  type="text"
                  value={weights.quality_weight}
                  onChange={(e) => handleChange('quality_weight', e.target.value)}
                  placeholder="0.00"
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* HSE Weight */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  color: '#333',
                  fontSize: '0.9rem'
                }}>
                  🦺 HSE Weight (%)
                </label>
                <input
                  type="text"
                  value={weights.hse_weight}
                  onChange={(e) => handleChange('hse_weight', e.target.value)}
                  placeholder="0.00"
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Performance Weight */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  color: '#333',
                  fontSize: '0.9rem'
                }}>
                  📈 Performance Weight (%)
                </label>
                <input
                  type="text"
                  value={weights.performance_weight}
                  onChange={(e) => handleChange('performance_weight', e.target.value)}
                  placeholder="0.00"
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Commercial Weight */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  color: '#333',
                  fontSize: '0.9rem'
                }}
                title="Commercial Documentation - contracts, pricing, terms, proposals"
                >
                  💼 Commercial Weight (%)
                </label>
                <input
                  type="text"
                  value={weights.commercial_weight}
                  onChange={(e) => handleChange('commercial_weight', e.target.value)}
                  placeholder="0.00"
                  disabled={saving}
                  title="Commercial Documentation - contracts, pricing, terms, proposals"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
                <small style={{ color: '#666', fontSize: '0.75rem' }}>
                  Commercial documentation (contracts, pricing, terms)
                </small>
              </div>

              {/* Technical Weight */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  color: '#333',
                  fontSize: '0.9rem'
                }}
                title="Technical Documentation - specifications, drawings, certifications, procedures"
                >
                  📐 Technical Weight (%)
                </label>
                <input
                  type="text"
                  value={weights.technical_weight}
                  onChange={(e) => handleChange('technical_weight', e.target.value)}
                  placeholder="0.00"
                  disabled={saving}
                  title="Technical Documentation - specifications, drawings, certifications, procedures"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
                <small style={{ color: '#666', fontSize: '0.75rem' }}>
                  Technical documentation (specs, drawings, certifications)
                </small>
              </div>

              {/* Total Display */}
              <div style={{
                backgroundColor: isValidTotal ? '#d4edda' : '#f8d7da',
                padding: '1rem',
                borderRadius: '4px',
                marginTop: '0.5rem',
                border: `1px solid ${isValidTotal ? '#c3e6cb' : '#f5c6cb'}`
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{
                    fontWeight: 'bold',
                    color: isValidTotal ? '#155724' : '#721c24'
                  }}>
                    Total Weight:
                  </span>
                  <span style={{
                    fontSize: '1.25rem',
                    fontWeight: 'bold',
                    color: isValidTotal ? '#155724' : '#721c24'
                  }}>
                    {totalWeight.toFixed(2)}%
                  </span>
                </div>
                {!isValidTotal && (
                  <div style={{
                    marginTop: '0.5rem',
                    fontSize: '0.85rem',
                    color: '#721c24'
                  }}>
                    ⚠️ Total must equal 100%
                  </div>
                )}
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleClose}
                disabled={saving}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  opacity: saving ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !isValidTotal}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: (saving || !isValidTotal) ? '#6c757d' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (saving || !isValidTotal) ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {saving && (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #fff',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                )}
                {saving ? 'Saving...' : 'Save Weights'}
              </button>
            </div>
          </>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
});

export default ContractorSelectionCriteriaModal;

